<template>
  <v-dialog v-model="dialog" width="800px" @keydown.esc="hide">
    <v-form>
      <v-card>
        <v-card-title class="card-panel teal lighten-2">
          {{ form.txID ? 'Транзакция ' + form.txID + (form.txPlanfix ? ' / planfix ref '+form.txPlanfix : '') : 'Новая транзакция' }}
        </v-card-title>

        <v-container>
          <v-row class="mx-0">
            <v-col cols="4" class="py-0">
              <v-text-field v-model.trim="form.txDt" persistent-hint hint="Дата"/>
            </v-col>
            <v-col cols="4" class="py-0">
              <v-select
                v-model="form.txType"
                :items="types"
                persistent-hint
                hint="Тип"/>
            </v-col>
            <v-col cols="4" class="py-0">
              <v-select
                v-model="form.txStatus"
                :items="statuses"
                persistent-hint
                hint="Статус"/>
            </v-col>
          </v-row>

          <v-row class="mx-0">
            <v-col cols="3" class="py-0">
              <v-text-field
                autofocus
                v-model="txAmount"
                persistent-hint
                hint="Сумма"
                :error-messages="$v.form.txAmount.$invalid ? 'Сумма' : ''"/>
            </v-col>
            <v-col cols="3" class="py-0">
              <v-text-field v-model.trim="form.txAmountNet" readonly persistent-hint hint="Сумма чистая"/>
            </v-col>
            <v-col cols="3" class="py-0">
              <v-text-field v-model.trim="form.txAmountTax" readonly persistent-hint hint="Налог"/>
            </v-col>
            <v-col cols="3" class="py-0">
              <v-text-field v-model.trim="form.txAmountFee" persistent-hint hint="Комиссия"/>
            </v-col>
          </v-row>

          <v-row class="mx-0">
            <v-col class="py-0">
              <v-autocomplete
                v-model="daCompanyID"
                :items="daCompaniesItems"
                persistent-hint
                hint="Контрагент DA"
                :error-messages="verrDaCompanyID"
                clearable
                class="input-da"/>
            </v-col>
          </v-row>

          <v-row class="mx-0">
            <v-col class="py-0">
              <v-autocomplete v-model="form.companyID" :items="companies" persistent-hint hint="Контрагент" clearable :error-messages="verrCompanyID"/>
            </v-col>
          </v-row>

          <v-row v-if="hasActivity" class="mx-0">
            <v-col class="py-0">
              <v-autocomplete v-model="form.activityID" :items="filteredActivities" persistent-hint hint="Активность" :error-messages="verrActivityID" clearable/>
            </v-col>
          </v-row>

          <v-row class="mx-0">
            <v-col class="pt-0">
              <v-text-field v-model.trim="form.txDetails" persistent-hint hint="Детали платежа" class="input-details" :error-messages="verrTxDetails"/>
            </v-col>
          </v-row>
        </v-container>

        <v-card-actions class="pb-3">
          <v-btn
            raised
            @click="hide()" class="ml-3">Отмена</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="form.txID && !form.txPlanfix"
            raised
            color="red"
            @click="deleteAction()" class="ml-3">Удалить</v-btn>
          <v-btn
            type="submit"
            raised
            color="primary"
            :loading="loading"
            :disabled="$v.$invalid"
            @click.prevent="submitAction()" class="mr-3">Сохранить</v-btn>
        </v-card-actions>

      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, requiredIf, decimal } from 'vuelidate/lib/validators'
import { combinedObject, txStatuses, txTypes } from '@/common'
import api from '@/api'

export default {
  props: [ 'companies', 'daCompanies' ],
  mixins: [ validationMixin ],
  data: () => ({
    dialog: false,
    loading: false,
    types: txTypes,
    statuses: txStatuses,
    activities: [],
    form: {},
    taxRate: 0
  }),
  computed: {
    daCompaniesItems: {
      get () { return this.daCompanies.map(i => ({value: i[0], text: i[1] + (i[2] ? ' ('+i[2]+'%'+(i[3] ? ' НДС)' : ')') : '')})) }
    },
    txAmount: {
      get () { return this.form.txAmount },
      set (v) { this.form.txAmount = v; this.recount() }
    },
    daCompanyID: {
      get () { return this.form.daCompanyID },
      set (v) { this.form.daCompanyID = v; this.recount() }
    },
    filteredActivities () {
      if ( !this.form.companyID || !this.form.daCompanyID ) return [];
      let c = this.companies.find(e => e.value == this.form.companyID)
      let da = this.daCompanies.find(e => e[0] == this.form.daCompanyID)
      c = c ? c.text : '?';
      da = da ? da[1] : '?';
      return this.activities.reduce((acc, cur) => (cur.companyID == this.form.companyID && cur.daCompanyID == this.form.daCompanyID ?
        acc.push({value: cur.activityID, text: c+' — '+da+' — '+cur.activityYear} ) : false, acc), [])
    },
    hasActivity () {
      return ['income', 'invoice'].includes(this.form.txType)
    },
    verrCompanyID () { return this.$v.form.companyID.$invalid ? 'Контрагент' : '' },
    verrDaCompanyID () { return this.$v.form.daCompanyID.$invalid ? 'Контрагент DA' : '' },
    verrActivityID () { return this.hasActivity && this.$v.form.activityID.$invalid ? 'Активность' : '' },
    verrTxDetails () { return this.$v.form.txDetails.$invalid ? 'Детали платежа' : '' }
  },
  validations: {
    form: {
      companyID: { required },
      daCompanyID: { required },
      activityID: { required: requiredIf(function(model) { return this.hasActivity }) },
      txAmount: { required, decimal },
      txDetails: { required },
    }
  },
  mounted () {
    api.get('finances/activities').then(response => {
      if ( response.data.$result !== 'ok' ) return this.$root.$alert(response.data.data, true)
      this.activities = combinedObject(response.data.data.cols, response.data.data.rows)
    }).catch(error => (this.$root.$alert(error, true)) )
  },
  methods: {
    recount () {
      let tax = 0
      let pdv = false
      let da = this.daCompanies.find(e => e[0] == this.form.daCompanyID)
      if ( da ) {
        tax = da[2]
        pdv = da[3]
      }

      this.form.txAmountTax = isNaN(tax) ? 0 : (pdv ? this.form.txAmount/(100+tax)*tax : this.form.txAmount * tax/100).toFixed(2)
      this.form.txAmountNet = isNaN(tax) ? this.form.txAmount : this.form.txAmount - this.form.txAmountTax
    },
    create () {
      let date = new Date()
      this.form = {
        daCompanyID: null,
        txDt: new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 19).replace('T', ' '),
        txType: 'income',
        txStatus: 'approved',
        txAmount: 0,
        txAmountNet: 0,
        txAmountTax: 0,
        txAmountFee: 0
      }
      this.dialog = true
    },
    edit (data) {
      api.get('finances/tx/'+data.txID).then(response => {
        if ( response.data.$result !== 'ok' ) return this.$root.$alert(response.data.data, true)
        this.form = Object.assign({}, response.data.data.tx)
        this.dialog = true
      }).catch(error => {
        this.$root.$alert(error, true)
      });
    },
    hide () {
      this.dialog = false
    },
    submitAction () {
      if ( this.$v.$invalid ) return
      this.loading = true
      api.post('finances/'+(this.form.txID ? 'tx/'+this.form.txID+'/update' : 'create'), this.form).then(response => {
        this.loading = false
        if ( response.data.$result !== 'ok' ) return this.$root.$alert(response.data.data, true)
        this.dialog = false
        this.$root.$alert('Транзакция ' + (this.form.txID ? 'сохранена' : 'добавлена'))
        this.$emit('updated')
      }).catch(error => (this.loading = false, this.$root.$alert(error, true)) )
    },
    deleteAction () {
      this.loading = true
      api.get('finances/tx/'+this.form.txID+'/delete').then(response => {
        this.loading = false
        if ( response.data.$result !== 'ok' ) return this.$root.$alert(response.data.data, true)
        this.dialog = false
        if ( response.data.data.affected > 0 ) this.$root.$alert('Транзакция удалена')
        this.$emit('updated')
      }).catch(error => (this.loading = false, this.$root.$alert(error, true)) )
    }
  }
}

</script>

<style>
.input-da .v-messages__message { color: #08A2B8; }
.input-details .v-messages__message { color: #7D3C98; }
</style>
